<template>
    <div class="report-toc">
        <b-menu>
            <b-menu-list :label="title">
                <b-autocomplete
                    v-if="searchBar"
                    v-model="searchTerm"
                    field="title"
                    icon="magnify"
                    placeholder="Search for sections"
                    keep-first
                    clearable
                    @typing="getSearchedSections"
                />
                <!-- We disable header rows so they can't be hovered or clicked -->
                <b-menu-item
                    v-for="section in searchBar ? searchedSections : sections"
                    :key="section.id"
                    :active="activeId === section.id"
                    :href="'#' + section.id"
                    :disabled="section.isHeader"
                    :class="{
                        'is-hidden-touch': hiddenOnSmallScreens.includes(section.id),
                        'header-row': section.isHeader,
                        'divider-after': section.dividerAfter,
                        'in-group': section.inGroup,
                    }"
                    @click="sethash($event, section.id)"
                >
                    <template slot="label">
                        {{ section.title }}
                    </template>
                </b-menu-item>
            </b-menu-list>
        </b-menu>
        <div class="buttons options">
            <slot name="options" />
            <b-button
                expanded
                tag="a"
                href="#map"
            >
                Back to map
            </b-button>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        /**
         * Title of table of contents.
         */
        title: {
            type: String,
            default: 'Table of Contents',
        },

        /**
         * Search bar of table of contents.
         */
        searchBar: {
            type: Boolean,
            default: false,
        },

        /**
         * Sections of table of contents.
         */
        sections: {
            type: Array,
            default: () => ([]),
        },

        /**
         * Dynamic filtered sections of table of contents.
         */
        filteredSections: {
            type: Array,
            default: () => ([]),
        },

        /**
         * Active Section IDs of table of contents.
         */
        activeId: {
            type: String,
            default: '',
        },

        /**
         * Small screens include screen tablet sized (up to 1023px according to bulma) and smaller.
         */
        hiddenOnSmallScreens: {
            type: Array,
            default: () => [],
        },
    },

    data: () => ({
        searchTerm: '',
        searchedSections: [],
    }),

    watch: {
        searchTerm() {
            this.getSearchedSections();
        },
    },

    created() {
        this.searchedSections = this.sections;
    },

    methods: {
        sethash(e, sectionId) {
            // update url hash
            window.history.replaceState({}, '', `#${sectionId}`);

            // scroll element into view, if it exists
            const el = document.getElementById(sectionId);
            if (el) {
                el.scrollIntoView(true);
            }

            e.preventDefault();
            e.stopPropagation();
        },

        /**
         * Filter table of contents sections by search term.
         * @param  {String} name search string
         * @param  {Boolean} sorted search sorting
         */
        filterSections(name, sorted = false) {
            // filter table of contents sections
            const filteredSections = this.sections.filter((section) => !section.isHeader
            && section.title
                .toLowerCase()
                .includes(name.toLowerCase()));

            // sort table of contents sections
            if (sorted) {
                filteredSections.sort((currentSection, nextSection) => {
                    if (currentSection.title < nextSection.title) return -1;
                    if (currentSection.title > nextSection.title) return 1;
                    return 0;
                });
            }
            return filteredSections;
        },

        /**
         * Search table of contents sections and emit "typing" event with filtered sections list.
         * @param  {String} name search string
         */
        getSearchedSections() {
            if (!this.searchTerm) {
                this.searchedSections = this.sections;
                this.$emit('typing', this.searchTerm, this.searchedSections);
                return;
            }

            // filter ToC sections
            this.searchedSections = this.filterSections(this.searchTerm);

            this.$emit('typing', this.searchTerm, this.searchedSections);
        },
    },
};
</script>

<style lang="scss">
    $toc-button-text-color: #fff !default;
    $toc-active-background-color: #3195f7 !default;
    $toc-background-color: #3d3254 !default;
    $toc-hover-color: #81768d !default;
    $toc-text-color: #fff !default;

    .report-toc {
        grid-column: 1/2;
        position: sticky;
        top: 0;
        height: 100vh; // THIS IS REQUIRED along with `top` for sticky to work right!
        width: 13rem;
        overflow-y: hidden;
        padding: 0;
        min-width: 8em;
        background-color: $toc-background-color;
        color: $toc-text-color;
        display: grid;
        grid-template-rows: 1fr auto;

        .menu {
            padding-top: 1rem;
            overflow-y: auto;

            .menu-label {
                margin-left: 0.6rem;
                margin-bottom: 0.5rem;
                color: #bbb;
                font-weight: 700;
                font-size: 1rem;
            }

            .autocomplete {
                padding: 0.5rem;
            }

            .menu-list {
                .is-active {
                    background-color: $toc-active-background-color;
                }

                .header-row {
                    a {
                        font-size: 0.9rem;
                        text-transform: uppercase;
                    }
                }

                li {
                    &.divider-after {
                        border-bottom: 1px solid #808080;
                        padding-bottom: 0.5rem;
                        margin-bottom: 0.5rem;
                    }

                    &.in-group a {
                        padding-left: 1.5rem;
                    }

                    a {
                        color: $toc-text-color;

                        &:hover {
                            background-color: $toc-hover-color;
                        }
                    }
                }
            }
        }

        .options {
            padding-bottom: 1rem;

            .button,
            .button:hover {
                color: $toc-button-text-color;
                background-color: $toc-active-background-color;
                border-color: transparent;
                margin: 0.75em 0.75em 0 0.75em;
                border-radius: 2px;
                box-shadow: none;

                .mdi {
                    height: 1.2em;
                }
            }
        }
    }

</style>
